import React from "react";
import { useTranslation } from "react-i18next";
import { SubPage, FullDivider } from "./SubPage";

export function Contact() {
  const { t } = useTranslation("contact");
  const steps = t("steps", { returnObjects: true }) as { step: string; label: string; desc: string }[];
  const founders = t("founders", { returnObjects: true }) as { name: string; role: string; bio: string; image: string; email: string; linkedin: string }[];

  return (
    <SubPage title={t("title")} subtitle={t("subtitle")} heroImage="/assets/team/founders-coffee.png" heroObjectPositionMobile="30% center">

      {/* Founders */}
      <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4">
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("foundersHeading")}</h2>
      </div>
      <FullDivider />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        {founders.map((f) => (
          <div key={f.name} className="bg-grey-975 flex flex-col">
            <div className="relative w-full aspect-[4/3] overflow-hidden">
              <img
                src={f.image}
                alt={f.name}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-grey-975 via-grey-975/20 to-transparent" />
            </div>
            <div className="px-5 sm:px-8 md:px-[66px] pb-10 md:pb-12 -mt-12 relative">
              <h3 className="text-lg font-semibold text-white">{f.name}</h3>
              <p className="text-xs font-mono text-grey-500 mt-0.5 mb-3">{f.role}</p>
              <p className="text-sm text-grey-400 leading-relaxed mb-4">{f.bio}</p>
              <div className="flex flex-wrap items-center gap-4">
                <a href={`mailto:${f.email}`} className="text-sm text-white/50 hover:text-white transition-colors flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
                  </svg>
                  {f.email}
                </a>
                <a href={f.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-white/50 hover:text-white transition-colors flex items-center gap-1.5">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="shrink-0">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="opacity-40"><path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" /></svg>
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
      <FullDivider />

      {/* Contact channels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-grey-800">
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <h3 className="text-base font-semibold text-white mb-3">{t("writeUsHeading")}</h3>
          <p className="text-sm text-grey-400 leading-relaxed">{t("writeUsBody")}</p>
        </div>
        <div className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12">
          <h3 className="text-base font-semibold text-white mb-4">{t("channelsHeading")}</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/30 mt-0.5 shrink-0">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M22 7l-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7" />
              </svg>
              <div>
                <p className="text-sm text-white/70 font-medium">{t("email")}</p>
                <a href="mailto:contact@cero-ai.com" className="text-sm text-white/40 hover:text-white transition-colors">contact@cero-ai.com</a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/30 mt-0.5 shrink-0">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" />
              </svg>
              <div>
                <p className="text-sm text-white/70 font-medium">{t("github")}</p>
                <a href="https://github.com/0-AI-UG" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-white transition-colors flex items-center gap-1">
                  github.com/0-AI-UG
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className="opacity-40"><path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.2" /></svg>
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/30 mt-0.5 shrink-0">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <div>
                <p className="text-sm text-white/70 font-medium">{t("location")}</p>
                <p className="text-sm text-white/40">{t("locationValue")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FullDivider />

      {/* What to expect */}
      <div className="relative">
        <div className="absolute inset-0 dot-grid-sm dot-fade-b pointer-events-none" style={{ opacity: 0.15 }} />
        <div className="px-5 sm:px-8 md:px-[66px] pt-12 md:pt-16 pb-4 relative">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{t("expectHeading")}</h2>
        </div>
        <FullDivider />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-grey-800 border-t border-grey-800 relative">
          {steps.map((s) => (
            <div key={s.step} className="bg-grey-975 px-5 sm:px-8 md:px-[66px] py-10 md:py-12 relative">
              <span className="text-[10px] font-mono text-grey-600 mb-4 block">{s.step}</span>
              <h3 className="text-base font-semibold text-white mb-2">{s.label}</h3>
              <p className="text-sm text-grey-400 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <FullDivider />

    </SubPage>
  );
}
